import { action, makeAutoObservable, reaction } from "mobx";
import { UpsertMembershipActiveStep } from "./Membership.enum"; 
import {
  BenefitsModel, 
  RelatedSectionModel, 
  ItemDetailInputModel,
  ServiceAndMembershipOptionsPageModel, 
  PriceBenefitsInputModel,
  PriceBenefitsValueMappingModel, 
  PriceProgramAndLevelsMappingModel,
  PricingPageInputModel, 
} from "../../shared/models";
import ProgramAndLevelsService from "./services/ProgramAndLevelsService";
import {  PricingPageService, RelatedItemsService } from "../../shared/services";
import { IAPICreateUpdateMembershipModel, IAPIGetMembershipForEditResponse, IAPIItemOptionsRequest } from "../../shared/interfaces";
import { AuthService } from "@mindmemobile/mm-shared-global";

export default class MembershipStore {
    public headerCaption: string = 'Detail';
    public activeStep: UpsertMembershipActiveStep = UpsertMembershipActiveStep.Detail;
    public pricingPageData: PricingPageService = new PricingPageService();
    public optionsPage: ServiceAndMembershipOptionsPageModel;
    public programAndLevelsService: ProgramAndLevelsService;
    public detailPage: ItemDetailInputModel;
    public showAddBenefitModal: boolean = false;
    public realtedItemService: RelatedItemsService;
    public isSaveUpdateInProgress: boolean = false;
    constructor() {
        this.optionsPage = new ServiceAndMembershipOptionsPageModel();
        this.programAndLevelsService = new ProgramAndLevelsService();
        this.realtedItemService = new RelatedItemsService();
        this.detailPage = new ItemDetailInputModel();
        makeAutoObservable(this);
        reaction(
            () => this.activeStep,
            () => this.updateStateBasedOnActiveStatusChange(),
        );
        reaction(
            () => this.pricingPageData.prices,
            () => {
                this.updateOptionsPageOnPriceOrBenefitsChange();
            },
        );
        reaction(
            () => this.optionsPage,
            () => {
                this.updateOptionsPageOnPriceOrBenefitsChange();
            },
        );
    }

    updateOptionsPage(data: ServiceAndMembershipOptionsPageModel) {
        this.optionsPage = new ServiceAndMembershipOptionsPageModel(data);
    }

    updateDetailsPage(data: ItemDetailInputModel) {
        this.detailPage = new ItemDetailInputModel(data);
    }

    updateOptionsPageOnPriceOrBenefitsChange() {
        const benefitInputs: PriceBenefitsInputModel[] = [];

        this.pricingPageData.prices.forEach((p) => {
            const allowTermBenefits = p.billingType === 'Term' || p.billingType === 'TermPlusRecurring';
            const allowRecurringBenefits = p.billingType === 'Recurring' || p.billingType === 'TermPlusRecurring';
            const matchedBenefit = this.optionsPage.benefits.find((x) => x.priceReferenceId === p.referenceId);
            if (matchedBenefit) {
                matchedBenefit.termBenefits = allowTermBenefits
                    ? this.updateBenefitMapping(this.optionsPage.selectedBenefits, matchedBenefit.termBenefits)
                    : [];
                matchedBenefit.recurringBenefits = allowRecurringBenefits
                    ? this.updateBenefitMapping(this.optionsPage.selectedBenefits, matchedBenefit.recurringBenefits)
                    : [];

                matchedBenefit.priceName = p.name;
                matchedBenefit.priceBillingType = p.billingType;
                matchedBenefit.isActivePrice = p.isActive;
                if (this.optionsPage.selectedBenefits.length > 0) {
                    benefitInputs.push(matchedBenefit);
                }
            } else {
                if (this.optionsPage.selectedBenefits.length > 0) {
                    benefitInputs.push(
                        new PriceBenefitsInputModel({
                            priceReferenceId: p.referenceId,
                            priceBillingType: p.billingType,
                            isActivePrice: p.isActive,
                            priceName: p.name,
                            termBenefits: allowTermBenefits
                                ? this.updateBenefitMapping(this.optionsPage.selectedBenefits, [])
                                : [],
                            recurringBenefits: allowRecurringBenefits
                                ? this.updateBenefitMapping(this.optionsPage.selectedBenefits, [])
                                : [],
                        }),
                    );
                }
            }
        });

        const programAndLevels: PriceProgramAndLevelsMappingModel[] = [];
        this.pricingPageData.prices.forEach((p) => {
            const matchedPL = this.optionsPage.programAndLevels.find((x) => x.priceReferenceId === p.referenceId);
            if (matchedPL) {
                matchedPL.isActivePrice = p.isActive;
                matchedPL.priceName = p.name;
                programAndLevels.push(matchedPL);
            } else {
                programAndLevels.push(
                    new PriceProgramAndLevelsMappingModel({
                        priceReferenceId: p.referenceId,
                        programId: '',
                        levelId: '',
                        priceName: p.name,
                        isActivePrice: p.isActive,
                    }),
                );
            }
        });
        this.optionsPage.programAndLevels = programAndLevels;
        this.optionsPage.benefits = benefitInputs;
    }

    updateBenefitMapping(benefits: BenefitsModel[], existingBenefits: PriceBenefitsValueMappingModel[]) {
        const mappedBenefits: PriceBenefitsValueMappingModel[] = [];
        benefits.forEach((b) => {
            const existingTermBenefits = existingBenefits.find((x) => x.benefitId === b.id);

            mappedBenefits.push(
                new PriceBenefitsValueMappingModel({
                    benefitId: b.id,
                    benefitName: b.pluralName,
                    value: existingTermBenefits?.value || '',
                    errors: existingTermBenefits?.errors || [],
                }),
            );
        });

        return mappedBenefits;
    }

    @action
    validate(step?: UpsertMembershipActiveStep): boolean {
        let isValid = true;
        const isTabChange = step ? true : false;
        const stepToValidate = isTabChange ? step : this.activeStep;
        switch (stepToValidate) {
            case UpsertMembershipActiveStep.Detail:
                isValid = this.detailPage.validate();
                this.detailPage = new ItemDetailInputModel(this.detailPage);
                break;
            case UpsertMembershipActiveStep.Pricing:
                isValid = this.pricingPageData.validate();
                this.pricingPageData.prices = this.pricingPageData.prices.map(
                    (price) => new PricingPageInputModel(price),
                );
                break;
            case UpsertMembershipActiveStep.Options:
                isValid = this.optionsPage.validate();
                if (!this.realtedItemService.relatedSectionData.validate()) {
                    isValid = false;
                    this.realtedItemService.relatedSectionData = new RelatedSectionModel({
                        ...this.realtedItemService.relatedSectionData,
                    });
                }
                this.optionsPage = new ServiceAndMembershipOptionsPageModel(this.optionsPage);
                break;
        }
        return isValid;
    }

    @action
    validatePreviousSteps(currentStep: UpsertMembershipActiveStep): {
        isValid: boolean;
        errorStep?: UpsertMembershipActiveStep;
    } {
        const steps = [
            UpsertMembershipActiveStep.Detail,
            UpsertMembershipActiveStep.Pricing,
            UpsertMembershipActiveStep.Options,
        ];
        const currentStepIndex = steps.indexOf(currentStep);
        for (let i = 0; i < currentStepIndex; i++) {
            const step = steps[i];
            if (!this.validate(step)) {
                return { isValid: false, errorStep: step };
            }
        }
        return { isValid: true };
    }

    updateActiveStep(step: UpsertMembershipActiveStep) {
        this.activeStep = step;
    }

    updateStateBasedOnActiveStatusChange() {
        switch (this.activeStep) {
            case UpsertMembershipActiveStep.Detail:
                this.headerCaption = 'Membership Details';
                break;
            case UpsertMembershipActiveStep.Pricing:
                this.headerCaption = 'Membership Pricing';
                break;
            case UpsertMembershipActiveStep.Options:
                this.headerCaption = 'Membership Options';
                break;
        }
    }

    reset() {
        this.headerCaption = 'Detail';
        this.activeStep = UpsertMembershipActiveStep.Detail;
        this.pricingPageData = new PricingPageService();
        this.optionsPage = new ServiceAndMembershipOptionsPageModel();
        this.programAndLevelsService = new ProgramAndLevelsService();
        this.realtedItemService = new RelatedItemsService();
        this.detailPage = new ItemDetailInputModel();
        this.isSaveUpdateInProgress = false;
        this.showAddBenefitModal = false;
    }

    toggleAddBenefitModal() {
        this.showAddBenefitModal = !this.showAddBenefitModal;
    }

    createAPIModel(): IAPICreateUpdateMembershipModel {
        const usage = this.optionsPage.createAPIModel();
        const relatedItems = this.realtedItemService.relatedSectionData.createAPIModel();
        const programAndLevels = this.optionsPage.createProgramAndLevelsAPIModel();
        const itemDetail = this.detailPage.createAPIModel();
        return {
            ...itemDetail,
            id: '',
            accountId: AuthService.authData.accountId,
            priceCreateData: this.pricingPageData.createAPIModel(),
            options: {
                relatedItems: relatedItems,
                usage: usage,
                programAndLevels: programAndLevels,
            } as IAPIItemOptionsRequest,
            isTrackInventory: false,
        } as IAPICreateUpdateMembershipModel;
    }

    constructStoreFromAPI(data: IAPIGetMembershipForEditResponse) {
        this.detailPage = ItemDetailInputModel.deserializeAPIModel(data);
        if (data.prices && data.prices?.length > 0) {
            this.pricingPageData.prices = PricingPageService.deserializeAPIModel(data.prices || []);
        }

        if (data.options) {
            this.optionsPage = ServiceAndMembershipOptionsPageModel.deserializeAPIModel(data.options);
        }
        if (data.options?.relatedItems) {
            this.realtedItemService.relatedSectionData = RelatedSectionModel.deserializeAPIModel(
                data.options.relatedItems,
            );
        }
    }

    toggleSaveUpdateInProgress() {
        this.isSaveUpdateInProgress = !this.isSaveUpdateInProgress;
    }
}
